import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { AxnAdminUser, AxnAdminUserOrganizationProperty } from "@idw-types/data/axn-admin-user";
import { RelyingPartyRole } from "@idw-types/common";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnAdminUserProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-property";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnAdminUserPropertyCategory } from "@idw-types/data/reference-data/generic";
import { AxnAdminUserRpProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-rp-property";
import { getRelyingPartyRolesWithPermissions } from "@utils/permissions-util";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { OrganizationPermissionsContainer } from "./organization-permissions-container/organization-permissions-container";
import { RelyingPartyPermissionsContainer } from "./relying-party-permissions-container/relying-party-permissions-container";
import styles from "./organization-user-details-permissions-panel-tab.less";

type OrganizationUserDetailsPermissionsPanelTabProps = {
    isDisabled: boolean;
    session: IDWSession;
    axnAdminUserState: AxnAdminUser;
    adminProperty: AxnAdminUserProperty;
    isCustomOrgRole: boolean;
    organizationRole: AxnAdminUserPropertyBundle;
    relyingPartyRoles: Array<RelyingPartyRole>;
    axnAdminUserPropertyCategories: Array<AxnAdminUserPropertyCategory>;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    axnRelyingParties: Array<AxnRelyingParty>;
    setAxnAdminUserState: (axnAdminUser: AxnAdminUser) => void;
};

export const OrganizationUserDetailsPermissionsPanelTab = (props: OrganizationUserDetailsPermissionsPanelTabProps) => {

    const {
        isDisabled,
        session,
        axnAdminUserState,
        adminProperty,
        isCustomOrgRole,
        organizationRole,
        relyingPartyRoles,
        axnRelyingParties,
        axnAdminUserPropertyCategories,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties,
        setAxnAdminUserState
    } = props;

    const relyingPartyRolesWithPermissions = useMemo(() => {
        return getRelyingPartyRolesWithPermissions(
            session,
            axnAdminUserState.id,
            axnRelyingParties,
            relyingPartyRoles
        ).filter(rprwp => rprwp.canView);
    }, [relyingPartyRoles, axnRelyingParties, session]);

    const addRelyingPartyProperties = (
        rpProperties: Array<AxnAdminUserRpProperty>,
        relyingParty: AxnRelyingParty
    ) => {
        const lastIndex = axnAdminUserState.axnAdminUserRPProperties
            .map(rpProperty => rpProperty.relyingPartyID === relyingParty.id)
            .lastIndexOf(true)

        if (lastIndex > 0) {
            const newRpProperties = axnAdminUserState.axnAdminUserRPProperties.slice();
            newRpProperties.splice(lastIndex, 0, ...rpProperties);
            setAxnAdminUserState({
                ...axnAdminUserState,
                axnAdminUserRPProperties: newRpProperties
            });
        }
    };

    const removeRelyingPartyProperties = (
        rpProperties: Array<AxnAdminUserRpProperty>,
        relyingParty: AxnRelyingParty
    ) => {
        const rpPropertyNames = rpProperties.map(rpP => rpP.propertyName);
        const newRpProperties = axnAdminUserState.axnAdminUserRPProperties.filter(property => {
            return (
                property.relyingPartyID !== relyingParty.id ||
                (
                    property.relyingPartyID === relyingParty.id &&
                    !rpPropertyNames.includes(property.propertyName)
                )
            );
        });

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserRPProperties: newRpProperties
        });
    };

    const addOrganizationProperties = (orgProperties: Array<AxnAdminUserOrganizationProperty>) => {
        const newOrgProperties = axnAdminUserState.axnAdminUserOrganizationProperties.slice();
        newOrgProperties.splice(newOrgProperties.length - 1, 0, ...orgProperties);

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserOrganizationProperties: newOrgProperties
        });
    };

    const removeOrganizationProperties = (orgProperties: Array<AxnAdminUserOrganizationProperty>) => {
        const orgPropertyNames = orgProperties.map(orgP => orgP.propertyName);
        const newOrgProperties = axnAdminUserState.axnAdminUserOrganizationProperties.filter(property => {
            return !orgPropertyNames.includes(property.propertyName);
        });

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserOrganizationProperties: newOrgProperties
        });
    };

    const resource = resources.pages.organizationUserDetails.tabs.permissionsPanel;

    return (
        <div>
            <ContentPageHeader title={resource.title} />
            <ContentPageBody isMaxWidth={true}>
                {adminProperty && (
                    <div>{resource.sections.admin.title}</div>
                )}
                {(organizationRole || isCustomOrgRole) && (
                    <div>
                        <div className={styles.sectionHeader}>
                            <h6>{resource.sections.organizationPermissions.title}</h6>
                        </div>
                        <OrganizationPermissionsContainer
                            isDisabled={isDisabled}
                            userId={axnAdminUserState.id}
                            organizationId={axnAdminUserState.organizationID}
                            axnAdminUserPropertyCategories={axnAdminUserPropertyCategories}
                            axnAdminAvailableUserOrganizationProperties={axnAdminAvailableUserOrganizationProperties}
                            axnAdminUserOrganizationProperties={axnAdminUserState.axnAdminUserOrganizationProperties}
                            addOrganizationProperties={addOrganizationProperties}
                            removeOrganizationProperties={removeOrganizationProperties}
                        />
                    </div>
                )}
                {!organizationRole && !isCustomOrgRole && relyingPartyRolesWithPermissions.length > 0 && (
                    <div>
                        <div className={styles.sectionHeader}>
                            <h6>{resource.sections.relyingPartyPermissions.title}</h6>
                        </div>
                        {relyingPartyRolesWithPermissions.map(rprwp => (
                            <RelyingPartyPermissionsContainer
                                key={`relying-party-permissions-container-${rprwp.relyingPartyRole.relyingParty.id}`}
                                isDisabled={isDisabled}
                                canModify={rprwp.canModify}
                                userId={axnAdminUserState.id}
                                isExpandedByDefault={relyingPartyRolesWithPermissions.length === 1}
                                relyingParty={rprwp.relyingPartyRole.relyingParty}
                                axnAdminUserPropertyCategories={axnAdminUserPropertyCategories}
                                axnAdminAvailableUserRPProperties={axnAdminAvailableUserRPProperties}
                                axnAdminUserRpProperties={axnAdminUserState.axnAdminUserRPProperties}
                                addRelyingPartyProperties={addRelyingPartyProperties}
                                removeRelyingPartyProperties={removeRelyingPartyProperties}
                            />
                        ))}
                    </div>
                )}
            </ContentPageBody>
        </div>
    );
}